import type { ReportInspection } from '@motorway/motorway-handshake-components/types';

export type DamageCategory = 'mechanical' | 'electrical' | 'cosmetic' | 'body' | 'documentation' | 'withdrawing' | 'seller' | 'Motorway issue';
export type DamageReasonText
  = | 'Additional damage'
    | 'Mechanical fault'
    | 'Documentation issue'
    | 'Withdrawing bid'
    | 'Seller reason'
    | 'Motorway issue';

const ADDITIONAL_DAMAGE = [
  'Damage to specific parts',
  'Glass damage',
  'Interior damage',
  'Paintwork',
  'Pets or smoked in',
  'Significant bodywork damage',
  'Wheel & tyre damage',
];

const MECHANICAL_FAULT = [
  'Engine management light',
  'Engine issues',
  'Transmission/gearbox issue',
  'Cooling system issue',
  'Suspension/steering issues',
  'Brake system faults',
  'Electrical faults',
  'Fault Codes',
  'ECU remap',
];

const DOCUMENTATION_ISSUE = [
  'Delay in providing necessary documents',
  'VAT and ownership discrepancies',
  'Mileage discrepancies',
  'Service history discrepancies',
  'Finance and warranty-related issues',
  'Background check issues',
];

const WITHDRAWING_BID = [
  'Bid in error',
  'My buyer has pulled out',
];

const SELLER_REASON = [
  'Seller cancelled',
  'Seller uncontactable',
  'Seller keeping car',
  'Seller sold-elsewhere',
  'Seller making unmanageable requests',
  'Seller misconduct',
];

const MOTORWAY_ISSUE = [
  'Listing discrepancy',
  'Motorway system issue',
];

export interface PriceAdjustmentReasonOption {
  text: DamageReasonText | 'Select an option';
  value: DamageCategory | '';
  disabled?: boolean;
  children?: readonly string[];
}

export const PLACEHOLDER_TEXT = 'Select an option';
const PLACEHOLDER = { text: PLACEHOLDER_TEXT, value: '', disabled: true } as PriceAdjustmentReasonOption;
const PLACEHOLDER_OPTION = { ...PLACEHOLDER, children: [PLACEHOLDER_TEXT] } as PriceAdjustmentReasonOption;

const SHARED_DAMAGE_OPTIONS: readonly PriceAdjustmentReasonOption[] = [
  { text: 'Additional damage', value: 'body', children: ADDITIONAL_DAMAGE },
  { text: 'Mechanical fault', value: 'mechanical', children: MECHANICAL_FAULT },
  { text: 'Documentation issue', value: 'documentation', children: DOCUMENTATION_ISSUE },
] as const;

const MOTORWAY_ISSUE_OPTION: PriceAdjustmentReasonOption = {
  text: 'Motorway issue',
  value: 'Motorway issue',
  children: MOTORWAY_ISSUE,
};

export const PRICE_ADJUSTMENT_REASON_OPTIONS: readonly PriceAdjustmentReasonOption[] = [
  PLACEHOLDER_OPTION,
  ...SHARED_DAMAGE_OPTIONS,
  MOTORWAY_ISSUE_OPTION,
] as const;

export const CANCELLATION_REASON_OPTIONS: readonly PriceAdjustmentReasonOption[] = [
  PLACEHOLDER_OPTION,
  ...SHARED_DAMAGE_OPTIONS,
  { text: 'Withdrawing bid', value: 'withdrawing', children: WITHDRAWING_BID },
  { text: 'Seller reason', value: 'seller', children: SELLER_REASON },
  MOTORWAY_ISSUE_OPTION,
] as const;

export const ADJUSTMENT_QUESTION = {
  ADJUSTMENT_AMOUNT: 'proposedPrice',
} as const satisfies Record<string, keyof ReportInspection>;

export const CANCELLATION_QUESTION_KEY = 'priceAdjustmentAttempted' as const satisfies keyof ReportInspection;
export const NEEDS_ON_SITE_SUPPORT_QUESTION_KEY = 'needsOnSiteSupport' as const satisfies keyof ReportInspection;

export const YES_NO_OPTIONS = [
  PLACEHOLDER,
  { text: 'Yes', value: 'true' },
  { text: 'No', value: 'false' },
];

export const FORM_KEYS = {
  ADJUSTMENT_DESCRIPTION: 'description',
  ADJUSTMENT_REASON: 'damageTypes',
  ADJUSTMENT_SPECIFIC: 'damageSpecifics',
  NEEDS_ON_SITE_SUPPORT: NEEDS_ON_SITE_SUPPORT_QUESTION_KEY,
} as const satisfies Record<string, keyof ReportInspection>;
